import React from 'react';
import { useGameState } from './hooks/useGameState';
import { useStories } from './hooks/useStories';
import { useUserProfile } from './hooks/useUserProfile';
import { supabase } from './lib/supabaseClient';
import { ChildProfileProvider, useChildProfile, ChildProfile } from './contexts/ChildProfileContext';
import HomeScreen from './components/HomeScreen';
import BooksScreen from './components/BooksScreen';
import ModernReadingScreen from './components/ModernReadingScreen';
import CollectionScreen from './components/CollectionScreen';
import WorldScreen from './components/WorldScreen';
import TransformationScreen from './components/TransformationScreen';
import WelcomeScreen from './components/Auth/WelcomeScreen';
import GuestLimitModal from './components/Auth/GuestLimitModal';
import ResetPasswordScreen from './components/Auth/ResetPasswordScreen';
import ParentSettingsScreen from './components/ParentSettingsScreen';
import ParentalPin from './components/ParentalPin';
import RewardModal from './components/RewardModal';
import ChildProfileSelector from './components/ChildProfileSelector';
import ChildProfileEditor from './components/ChildProfileEditor';
import EmailConfirmationBanner from './components/EmailConfirmationBanner';
import SubscriptionScreen from './components/SubscriptionScreen';
import SubscriptionModal from './components/Auth/SubscriptionModal';
import { Book, Gradients, CollectibleObject } from './types';
import { LanguageProvider } from './contexts/LanguageContext';
import { TranslationProvider } from './contexts/TranslationContext';
import { MotionPermissionProvider, useMotionPermission } from './contexts/MotionPermissionContext';
import LanguageChangeIndicator from './components/LanguageChangeIndicator';
import LanguageModal from './components/LanguageModal';
import './App.css';

const gradients: Gradients = {
  'sky-purple': {
    light: 'from-sky-100 via-blue-200 to-purple-300',
    normal: 'from-sky-200 via-blue-300 to-purple-400',
    dark: 'from-sky-400 via-blue-500 to-purple-600'
  },
  'sunset': {
    light: 'from-orange-100 via-pink-200 to-red-300',
    normal: 'from-orange-200 via-pink-300 to-red-400',
    dark: 'from-orange-400 via-pink-500 to-red-600'
  },
  'ocean': {
    light: 'from-blue-100 via-cyan-200 to-teal-300',
    normal: 'from-blue-200 via-cyan-300 to-teal-400',
    dark: 'from-blue-400 via-cyan-500 to-teal-600'
  },
  'forest': {
    light: 'from-green-100 via-emerald-200 to-lime-300',
    normal: 'from-green-200 via-emerald-300 to-lime-400',
    dark: 'from-green-400 via-emerald-500 to-lime-600'
  },
  'space': {
    light: 'from-purple-200 via-indigo-300 to-black',
    normal: 'from-purple-400 via-indigo-500 to-gray-900',
    dark: 'from-purple-600 via-indigo-700 to-black'
  }
};

const AppContent: React.FC = () => {
  // Log pour détecter les reloads complets
  React.useEffect(() => {
    console.log('🚀 APP MOUNTED - Si tu vois ça pendant la lecture, c\'est un RELOAD COMPLET');
  }, []);

  // Check authentication
  const { user, profile, loading: authLoading, isAuthenticated, emailConfirmed, authProvider } = useUserProfile();
  const { activeChild, setActiveChild, children: childProfiles, loading: childrenLoading } = useChildProfile();
  const [hasSeenWelcome, setHasSeenWelcome] = React.useState(false);
  const [showGuestLimitModal, setShowGuestLimitModal] = React.useState(false);
  const [guestLimitFeature, setGuestLimitFeature] = React.useState<'rewards' | 'collection' | 'heroes' | 'world' | 'transformation'>('collection');
  const [showResetPassword, setShowResetPassword] = React.useState(false);
  const [showParentSettings, setShowParentSettings] = React.useState(false);
  const [showPinVerification, setShowPinVerification] = React.useState(false);
  const [showChildEditor, setShowChildEditor] = React.useState(false);
  const [editingChild, setEditingChild] = React.useState<ChildProfile | undefined>(undefined);
  const [showSubscriptionScreen, setShowSubscriptionScreen] = React.useState(false);
  const [languageConfirmed, setLanguageConfirmed] = React.useState(false);

  // Détecter si on arrive avec un token de reset password
  React.useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const type = hashParams.get('type');

    // Si c'est un lien de récupération de mot de passe
    if (type === 'recovery') {
      setShowResetPassword(true);
    }
  }, []);

  // Détecter retour de Stripe checkout
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const isSuccessPage = window.location.pathname.includes('subscription-success');

    console.log('🔍 Stripe return check:', { sessionId, isSuccessPage, userId: user?.id, authLoading });

    // Attendre que l'auth soit chargé
    if (authLoading) return;

    if (sessionId && isSuccessPage && user?.id) {
      console.log('✅ Processing Stripe session...');
      // Vérifier la session Stripe et activer l'abonnement
      const verifySession = async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/stripe-webhook`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
              },
              body: JSON.stringify({
                action: 'verify-session',
                sessionId,
                userId: user.id,
              }),
            }
          );

          const data = await response.json();

          if (data.success) {
            // Nettoyer l'URL et recharger pour avoir le profil à jour
            window.history.replaceState({}, document.title, '/');
            window.location.reload();
          } else {
            console.error('Session verification failed:', data.error);
            window.history.replaceState({}, document.title, '/');
          }
        } catch (err) {
          console.error('Failed to verify Stripe session:', err);
          window.history.replaceState({}, document.title, '/');
        }
      };

      verifySession();
    }
  }, [user?.id, authLoading]);

  // Reward modal state
  const [showRewardModal, setShowRewardModal] = React.useState(false);
  const [rewardModalData, setRewardModalData] = React.useState<{
    rewards: CollectibleObject[];
    bookTitle: string;
    isReplay: boolean;
    syncConflict?: boolean;
    error?: string;
  }>({
    rewards: [],
    bookTitle: '',
    isReplay: false
  });

  const {
    gameState,
    allObjects,
    userInstances,
    loading,
    setCurrentScreen,
    setSelectedBook,
    setCurrentPage,
    setActiveTab,
    setIsPremium,
    setEditMode,
    setSelectedElement,
    setSelectedHero,
    completeBook,
    addObjectToHome,
    removeObjectFromHome,
    updateHomeObject,
    updateHeroConfig,
    transformObject,
    setBackgroundConfig,
    setLogoConfig,
    saveStoryProgress,
    clearStoryProgress
  } = useGameState({ childId: activeChild?.id });

  // Note: Premium access is now checked via useSubscription hook in child components
  // The subscriptions table is the single source of truth



  // Charger les histoires depuis l'API
  // RLS policy automatically filters based on user's subscription_tier:
  // - Guest/Free users: only visible + non-premium stories
  // - Premium users: all visible stories (free + premium)
  const { stories, loading: storiesLoading, error: storiesError } = useStories();
  const freeBooks = stories.filter(book => !book.premium);
  const selectedHeroData = gameState.heroes.find(h => h.id === gameState.selectedHero);

  // Note: Element filtering is now handled by RLS policy at database level
  // Migration 020_rls_filter_elements_by_visible_stories.sql ensures only elements
  // from visible stories (+ default unlocked heroes) are returned by useObjects/useHeroes

  // Handler for opening settings - checks PIN if configured
  const handleOpenSettings = () => {
    if (profile?.parentPinHash) {
      // PIN is configured - require verification
      setShowPinVerification(true);
    } else {
      // No PIN - open settings directly
      setShowParentSettings(true);
    }
  };

  // Handler for subscription completion
  const handleSubscriptionComplete = async (plan: 'monthly' | 'yearly') => {
    if (!user) return;

    const now = new Date();

    // Trial period: 7 days from now (for iOS refund period)
    const trialEndsAt = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    // Subscription expiration based on plan
    const subscriptionExpiresAt = plan === 'yearly'
      ? new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000)
      : new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

    // Update user profile with trial status (becomes 'active' after trial)
    const { error } = await supabase
      .from('user_profiles')
      .update({
        subscription_status: 'trial', // Start with trial for refund period
        subscription_tier: 'premium',
        subscription_plan: plan,
        subscription_expires_at: subscriptionExpiresAt.toISOString(),
        subscription_start_date: now.toISOString(),
        trial_ends_at: trialEndsAt.toISOString(),
      })
      .eq('id', user.id);

    if (error) {
      console.error('Error updating subscription:', error);
    }

    // Close subscription screen and reload to get updated profile
    setShowSubscriptionScreen(false);
    window.location.reload();
  };

  // Show reset password screen if coming from email link
  if (showResetPassword) {
    return (
      <ResetPasswordScreen
        onComplete={() => {
          setShowResetPassword(false);
          // Nettoyer l'URL
          window.history.replaceState({}, document.title, window.location.pathname);
        }}
      />
    );
  }

  // Show PIN verification modal
  if (showPinVerification) {
    return (
      <ParentalPin
        mode="verify"
        currentPinHash={profile?.parentPinHash}
        userEmail={user?.email}
        authProvider={authProvider}
        onSuccess={() => {
          setShowPinVerification(false);
          setShowParentSettings(true);
        }}
        onCancel={() => setShowPinVerification(false)}
        onPinSet={async (pinHash) => {
          // Called when PIN is reset after password verification
          const { error } = await supabase
            .from('user_profiles')
            .update({ parent_pin_hash: pinHash })
            .eq('id', user!.id);

          if (error) {
            console.error('Error saving PIN:', error);
            throw error;
          }

          // Refresh to get updated profile
          window.location.reload();
        }}
      />
    );
  }

  // Show parent settings screen
  if (showParentSettings) {
    return (
      <ParentSettingsScreen
        onBack={() => setShowParentSettings(false)}
      />
    );
  }

  // Show loading screen while auth or APIs are loading
  if (authLoading || loading || storiesLoading || childrenLoading) {
    console.warn('⚠️ Loading screen triggered during session:', {
      authLoading,
      loading,
      storiesLoading,
      childrenLoading,
      currentScreen: gameState.currentScreen,
      hasSelectedBook: !!gameState.selectedBook
    });
    return (
      <div className="h-screen-adaptive flex items-center justify-center bg-gradient-to-b from-purple-600 to-purple-800">
        <div className="text-center">
          <img
            src={`${process.env.PUBLIC_URL}/logo-white.png`}
            alt="LilHero"
            className="w-32 h-32 mx-auto mb-6 animate-pulse"
          />
          <p className="text-white/80 text-sm">
            {authLoading ? 'Connexion...' : 'Chargement...'}
          </p>
        </div>
      </div>
    );
  }

  // Show welcome screen if not authenticated AND hasn't seen welcome yet
  if (!isAuthenticated && !hasSeenWelcome) {
    return <WelcomeScreen onContinueAsGuest={() => setHasSeenWelcome(true)} />;
  }

  // Show child profile editor
  if (showChildEditor) {
    return (
      <ChildProfileEditor
        child={editingChild}
        onSave={() => {
          setShowChildEditor(false);
          setEditingChild(undefined);
        }}
        onCancel={() => {
          setShowChildEditor(false);
          setEditingChild(undefined);
        }}
        onDelete={() => {
          setShowChildEditor(false);
          setEditingChild(undefined);
        }}
      />
    );
  }

  // MANDATORY SETUP: Force parent to confirm language and create PIN after email confirmation
  // This ensures parental controls are always set up for new accounts
  const needsMandatorySetup = isAuthenticated && emailConfirmed && profile && !profile.parentPinHash;

  // Step 1: Language confirmation (before PIN setup)
  if (needsMandatorySetup && !languageConfirmed) {
    return (
      <div className="h-screen-adaptive bg-gradient-to-b from-blue-50 to-purple-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="mb-6">
            <span className="text-6xl">🌍</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Bienvenue sur LilHero !</h1>
          <p className="text-gray-600 mb-6">Choisissez la langue de l'application</p>
          <LanguageModal
            isOpen={true}
            onClose={() => setLanguageConfirmed(true)}
            title="Choisir la langue"
            showCloseButton={false}
            onLanguageSelected={() => setLanguageConfirmed(true)}
          />
        </div>
      </div>
    );
  }

  // Step 2: PIN setup (after language confirmed)
  if (needsMandatorySetup && languageConfirmed) {
    return (
      <ParentalPin
        mode="setup"
        required={true}
        title="Sécurisez votre compte"
        subtitle="Créez un code PIN parental à 4 chiffres pour protéger les paramètres et les achats"
        onSuccess={() => {
          // Profile will be updated by onPinSet, no need to do anything here
        }}
        onPinSet={async (pinHash) => {
          // Update profile with new PIN hash
          const { error } = await supabase
            .from('user_profiles')
            .update({ parent_pin_hash: pinHash })
            .eq('id', user!.id);

          if (error) {
            console.error('Error saving PIN:', error);
            throw error;
          }

          // Refresh the profile to get the updated parentPinHash
          window.location.reload();
        }}
      />
    );
  }

  // Show subscription screen if requested
  if (showSubscriptionScreen) {
    return (
      <SubscriptionScreen
        onBack={() => setShowSubscriptionScreen(false)}
        onSubscriptionComplete={handleSubscriptionComplete}
      />
    );
  }


  // Show child profile selector for authenticated users who haven't selected a child yet
  // (or when they want to switch profiles)
  if (isAuthenticated && !activeChild) {
    return (
      <ChildProfileSelector
        onSelectChild={(child) => {
          setActiveChild(child);
        }}
        onAddChild={() => {
          setEditingChild(undefined);
          setShowChildEditor(true);
        }}
        onManageSettings={() => setShowParentSettings(true)}
      />
    );
  }

  // Show error if stories failed to load
  if (storiesError) {
    console.error('❌ Erreur de chargement des histoires:', storiesError);
    // Continue avec un tableau vide plutôt que de bloquer l'app
  }

  const handleBookRead = (book: Book) => {
    setSelectedBook(book);
    setCurrentPage(0);
    setCurrentScreen('reading');
  };

  const handleBookComplete = async (book: Book, visitedRewardIds: number[] = []) => {
    // Guest users: show modal to encourage account creation instead of giving rewards
    if (!isAuthenticated) {
      setGuestLimitFeature('rewards');
      setShowGuestLimitModal(true);
      setCurrentScreen('books');
      return;
    }

    // Debug: Log book data
    console.log('📖 Book completed:', book.title);
    console.log('🎁 Visited reward IDs:', visitedRewardIds);

    // Vérifier d'abord localement (pour UX rapide)
    const isLocallyCompleted = gameState.completedBooks.includes(book.id);

    if (isLocallyCompleted) {
      console.log('⚠️ Livre déjà lu localement - pas de nouvelles récompenses');
      // Relecture: pas de rewards, juste mettre à jour last_read_at
      await completeBook(book);
      setRewardModalData({
        rewards: [],
        bookTitle: book.title,
        isReplay: true
      });
      setShowRewardModal(true);
      return;
    }

    // Première lecture locale - appeler le serveur pour vérifier et obtenir les récompenses
    console.log('✨ Première lecture - tentative d\'attribution des récompenses');
    const result = await completeBook(book, visitedRewardIds);

    // Si le serveur dit que c'était déjà complété (sync conflit avec autre device)
    if (result.alreadyCompleted) {
      console.log('⚠️ Conflit détecté - histoire déjà complétée sur un autre appareil');
      setRewardModalData({
        rewards: [],
        bookTitle: book.title,
        isReplay: true,
        syncConflict: true // Nouveau flag pour message spécifique
      });
      setShowRewardModal(true);
      return;
    }

    // Récompenses accordées avec succès
    if (result.rewardsGranted) {
      const rewardObjects: CollectibleObject[] = visitedRewardIds
        .map(rewardId => allObjects.find(obj => obj.id === rewardId))
        .filter((obj): obj is CollectibleObject => obj !== undefined);

      console.log('🎁 Reward objects granted:', rewardObjects);
      setRewardModalData({
        rewards: rewardObjects,
        bookTitle: book.title,
        isReplay: false
      });
      setShowRewardModal(true);
    } else {
      // Erreur réseau ou autre - afficher quand même un message
      console.warn('⚠️ Récompenses non accordées:', result.message);
      setRewardModalData({
        rewards: [],
        bookTitle: book.title,
        isReplay: false,
        error: result.message
      });
      setShowRewardModal(true);
    }
  };

  const handleObjectClick = (objectData: any) => {
    alert(`${objectData.name} ✨`);
  };

  const handleHeroClick = () => {
    if (!gameState.editMode) {
      setCurrentScreen('heroes');
    }
  };

  const handleElementSelect = (element: any) => {
    setSelectedElement(element);
  };

  const handleObjectToHome = (instanceId: number) => {
    const newHomeObject = addObjectToHome(instanceId);
    if (newHomeObject) {
      // Activer le mode édition si pas déjà actif
      if (!gameState.editMode) {
        setEditMode(true);
      }
      // Présélectionner l'objet ajouté
      setSelectedElement({ type: 'object', id: newHomeObject.id, data: newHomeObject });
      // Aller à WorldScreen
      setCurrentScreen('world');
    }
  };

  // Rendu conditionnel basé sur l'écran actuel
  const screenContent = (() => {
  switch (gameState.currentScreen) {
    case 'home':
      return (
        <HomeScreen
          gameState={gameState}
          allObjects={allObjects}
          userInstances={userInstances}
          selectedHeroData={selectedHeroData}
          gradients={gradients}
          onScreenChange={setCurrentScreen}
          onObjectClick={handleObjectClick}
          onHeroClick={handleHeroClick}
          onElementSelect={handleElementSelect}
          onOpenSettings={handleOpenSettings}
          onSwitchProfile={() => setActiveChild(null)}
          activeChildName={activeChild?.name}
        />
      );

    case 'books':
      return (
        <BooksScreen
          gameState={gameState}
          books={stories}
          freeBooks={freeBooks}
          allObjects={allObjects}
          onBack={() => setCurrentScreen('home')}
          onBookRead={handleBookRead}
          onTabChange={setActiveTab}
          onPremiumUpgrade={() => setIsPremium(true)}
          isGuestMode={!isAuthenticated}
          onRequestAuth={() => {
            setHasSeenWelcome(false);
            // Retour au WelcomeScreen qui affichera login/signup
          }}
          onSubscribe={() => setShowSubscriptionScreen(true)}
        />
      );

    case 'reading':
      if (!gameState.selectedBook) {
        console.error('⚠️ BUG DETECTED: selectedBook is null during reading! Redirecting to books.', {
          currentScreen: gameState.currentScreen,
          timestamp: new Date().toISOString()
        });
        setCurrentScreen('books');
        return null;
      }
      return (
        <ModernReadingScreen
          gameState={gameState}
          selectedBook={gameState.selectedBook}
          onBack={() => setCurrentScreen('books')}
          onPageChange={(pageKey: string) => {
            // Modern reading screen handles page navigation internally
            // This callback is for compatibility but not used the same way
          }}
          onBookComplete={handleBookComplete}
          onSaveProgress={saveStoryProgress}
          onClearProgress={clearStoryProgress}
        />
      );

    case 'collection':
    case 'heroes': // Heroes merged into Collection screen
      // Guest users: show modal to encourage account creation
      if (!isAuthenticated) {
        setGuestLimitFeature('collection');
        setShowGuestLimitModal(true);
        setCurrentScreen('home');
        return null;
      }
      return (
        <CollectionScreen
          gameState={gameState}
          allObjects={allObjects}
          userInstances={userInstances}
          books={stories}
          onBack={() => setCurrentScreen('home')}
          onBookRead={handleBookRead}
          onObjectToHome={handleObjectToHome}
          onHeroSelect={setSelectedHero}
        />
      );

    case 'world':
      // Guest users: show modal to encourage account creation
      if (!isAuthenticated) {
        setGuestLimitFeature('world');
        setShowGuestLimitModal(true);
        setCurrentScreen('home');
        return null;
      }
      // Activer automatiquement le mode édition quand on accède à WorldScreen
      if (!gameState.editMode) {
        setEditMode(true);
        setSelectedElement(null);
      }
      return (
        <WorldScreen
          gameState={gameState}
          allObjects={allObjects}
          userInstances={userInstances}
          selectedHeroData={selectedHeroData}
          gradients={gradients}
          onBack={() => {
            setEditMode(false);
            setSelectedElement(null);
            setCurrentScreen('home');
          }}
          onEditModeToggle={() => {
            setEditMode(!gameState.editMode);
            setSelectedElement(null);
          }}
          onElementSelect={setSelectedElement}
          onHeroConfigUpdate={updateHeroConfig}
          onBackgroundConfigUpdate={setBackgroundConfig}
          onLogoConfigUpdate={setLogoConfig}
          onObjectToHome={addObjectToHome}
          onUpdateHomeObject={updateHomeObject}
          onRemoveHomeObject={removeObjectFromHome}
          onHeroSelect={setSelectedHero}
        />
      );

    case 'transformation':
      // Guest users: show modal to encourage account creation
      if (!isAuthenticated) {
        setGuestLimitFeature('transformation');
        setShowGuestLimitModal(true);
        setCurrentScreen('home');
        return null;
      }
      return (
        <TransformationScreen
          gameState={gameState}
          allObjects={allObjects}
          onBack={() => setCurrentScreen('home')}
          onTransform={transformObject}
        />
      );

    default:
      return (
        <div className="h-screen-adaptive flex items-center justify-center bg-gray-100">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-800 mb-4">
              Erreur - Écran non trouvé
            </h1>
            <button
              onClick={() => setCurrentScreen('home')}
              className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      );
  }
  })();

  return (
    <>
      {/* Email confirmation banner for authenticated but unconfirmed users */}
      {isAuthenticated && !emailConfirmed && (
        <EmailConfirmationBanner email={user?.email} />
      )}

      {screenContent}

      {/* Guest Limit Modal */}
      {showGuestLimitModal && (
        <GuestLimitModal
          feature={guestLimitFeature}
          onClose={() => setShowGuestLimitModal(false)}
          onCreateAccount={() => {
            setShowGuestLimitModal(false);
            setHasSeenWelcome(false);
            // This will trigger WelcomeScreen to show signup
          }}
          onLogin={() => {
            setShowGuestLimitModal(false);
            setHasSeenWelcome(false);
            // This will trigger WelcomeScreen to show login
          }}
        />
      )}

      {/* Reward Modal */}
      {showRewardModal && (
        <RewardModal
          rewards={rewardModalData.rewards}
          bookTitle={rewardModalData.bookTitle}
          isReplay={rewardModalData.isReplay}
          syncConflict={rewardModalData.syncConflict}
          onClose={() => {
            setShowRewardModal(false);
            setCurrentScreen('collection');
          }}
        />
      )}

    </>
  );
};

// Composant pour le modal de permission motion
const MotionPermissionModal: React.FC = () => {
  const {
    showPermissionModal,
    setShowPermissionModal,
    requestMotionPermission,
    needsMotionPermission,
    isNative
  } = useMotionPermission();

  // Ne rien afficher si pas nécessaire
  if (!showPermissionModal || !isNative || !needsMotionPermission) {
    return null;
  }

  const handleActivate = async () => {
    await requestMotionPermission();
    setShowPermissionModal(false);
  };

  return (
    <div className="fixed inset-0 z-[30000] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl p-6 mx-4 max-w-sm shadow-2xl text-center">
        <div className="text-5xl mb-4">📱</div>
        <h3 className="text-lg font-bold text-gray-800 mb-2">
          Activer les effets de mouvement ?
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          Pour profiter des effets de parallaxe dans les histoires, autorise l'accès au capteur de mouvement.
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => setShowPermissionModal(false)}
            className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
          >
            Plus tard
          </button>
          <button
            onClick={handleActivate}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:from-purple-600 hover:to-pink-600 transition-colors shadow-lg"
          >
            Activer
          </button>
        </div>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <LanguageProvider>
      <TranslationProvider>
        <MotionPermissionProvider>
          <ChildProfileProvider>
            <div className="relative">
              <AppContent />
              <LanguageChangeIndicator />
              <MotionPermissionModal />
            </div>
          </ChildProfileProvider>
        </MotionPermissionProvider>
      </TranslationProvider>
    </LanguageProvider>
  );
};

export default App;